import React, { useEffect, useState } from 'react';
import * as Unicons from '@iconscout/react-unicons';
import AdminLayout from '../../components/layout/admin';
import Breadcrumbs from '../../components/elements/Breadcrumbs';
import {
  AppShell,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  Text
} from '@mantine/core';
import dayjs from 'dayjs';
import { dateFormat } from '../../utilities/dataFormat';
import { useLocation } from 'react-router-dom';
import Button from '../../components/elements/Button';
import { FiEdit3, FiX } from 'react-icons/fi';
import { useMutation, useQuery } from '@tanstack/react-query';
import useLocalStorage from '../../hooks/useUserData';
import { useDisclosure } from '@mantine/hooks';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import Draggable, { WidgetConfig } from '../../components/elements/Draggable';
import { showNotification } from '../../utilities/mantineNotification';
import ModalConfirmation from '../../components/fragments/ModalConfirmation';
import WidgetList from './WidgetList';
import { dashboardWidget } from '../../constants/defaultValues/dashboardWidget';
import FilterDashboard from '../../components/fragments/FilterDashboardPrincipal';
import {
  getListWidget,
  postWidgetConfiguration
} from '../../services/dashboardPrincipal';

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const DashboardPrincipalPage = () => {
  const [userData, setUserData] = useLocalStorage('persist:root', null);
  const data = JSON.parse(userData?.auth);
  const [openModal, { toggle: toggleModal }] = useDisclosure(false);
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(false);
  const [queryParams, setQueryParams] = useState({
    startDate: dayjs().subtract(1, 'month').format(dateFormat('filter-format')),
    endDate: dayjs().format(dateFormat('filter-format')),
    categoryId: '',
    yearFrom: dayjs().get('year').toString(),
    yearTo: dayjs().get('year').toString(),
    typeTask: ''
  });
  const [itemsList, setItemsList] = useState<WidgetConfig[]>([]);

  const fetchedConfigurationDashboard = useQuery({
    queryKey: ['configuration-dashboard-principal', data?.userData?.binusianId],
    queryFn: () =>
      getListWidget({
        binusianId: data?.userData?.binusianId
      }),
    enabled: !!data?.userData?.binusianId
  });

  const updateWidgetConfiguration = useMutation({
    mutationFn: () =>
      postWidgetConfiguration({
        binusianId: data?.userData?.binusianId || '',
        data: itemsList
      }),
    onSuccess: () => {
      fetchedConfigurationDashboard.refetch();
      showNotification({
        type: 'success',
        title: 'Success Update Widget',
        message: 'Success! widget configuration has been updated!'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        title: 'Failed Update Widget',
        message: `Oops! We ran into a snag while trying to update widget configuration. Please try again in a few moments.`
      });
    }
  });

  const location = useLocation();

  const queryParamLocation: any = new URLSearchParams(location.search);

  const paramsObject = {
    startDate: queryParamLocation.get('startDate'),
    endDate: queryParamLocation.get('endDate'),
    categoryId: queryParamLocation.get('categoryId')
  };

  useEffect(() => {
    if (
      fetchedConfigurationDashboard?.data?.data?.data &&
      fetchedConfigurationDashboard?.data?.data?.data?.length > 0 &&
      itemsList.length === 0
    ) {
      setItemsList(
        fetchedConfigurationDashboard?.data?.data?.data.sort(
          (a, b) => a.posisi - b.posisi
        )
      );
      return;
    }
  }, [fetchedConfigurationDashboard?.data?.data]);

  useEffect(() => {
    if (
      paramsObject?.startDate ||
      paramsObject?.endDate ||
      paramsObject?.categoryId
    ) {
      setQueryParams((prevState) => ({ ...prevState, ...paramsObject }));
    }

    if (
      !paramsObject?.startDate &&
      !paramsObject?.endDate &&
      !paramsObject?.categoryId
    ) {
      setQueryParams((prevState) => ({
        ...prevState,
        startDate: dayjs()
          .subtract(1, 'month')
          .format(dateFormat('filter-format')),
        endDate: dayjs().format(dateFormat('filter-format'))
      }));
    }
  }, [
    paramsObject?.startDate,
    paramsObject?.endDate,
    paramsObject?.categoryId
  ]);

  const handleDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items: any = reorder(
      itemsList,
      result.source.index,
      result.destination.index
    );

    items.forEach((item: WidgetConfig, index: number) => {
      item.posisi = index + 1;
    });

    setItemsList(items);
  };

  const handleHideItem = (item: WidgetConfig) => {
    const items = itemsList.map((i) => {
      if (i.title === item.title) {
        return { ...i, display: !i.display };
      }
      return i;
    });

    setItemsList(items);
  };

  const handleUpdateWidget = () => {
    updateWidgetConfiguration.mutate();
    toggleModal();
    toggleDesktop();
  };

  const onUpdate = () => {
    toggleModal();
  };

  const onCancel = () => {
    setItemsList(fetchedConfigurationDashboard?.data?.data?.data);
    toggleDesktop();
  };

  return (
    <>
      <AdminLayout>
        <AppShell
          aside={{
            width: 300,
            breakpoint: 'sm',
            collapsed: { desktop: !desktopOpened }
          }}
          padding="md"
        >
          <AppShell.Main p={0}>
            <Grid gutter={0}>
              <Grid.Col span={12}>
                <Group align="center" flex={1} justify="space-between">
                  <Breadcrumbs
                    pageTitle="Dashboard Principal"
                    itemList={[
                      {
                        title: 'Team Learning',
                        href: '#',
                        icon: <Unicons.UilFileCopyAlt size={16} />
                      },
                      {
                        title: 'Dashboard Principal',
                        href: '#'
                      }
                    ]}
                  />

                  {/* <Button
                    variant="transparent"
                    leftSection={<FiEdit3 />}
                    onClick={() => toggleDesktop()}
                  >
                    Change Widget
                  </Button> */}
                </Group>
                <FilterDashboard filterData={queryParams} />
                <WidgetList
                  categoryId={queryParams?.categoryId}
                  endDate={queryParams?.endDate}
                  startDate={queryParams?.startDate}
                  yearFrom={queryParams?.yearFrom}
                  yearTo={queryParams?.yearTo}
                  typeTask={queryParams?.typeTask}
                  itemsList={itemsList.length > 0 ? itemsList : dashboardWidget}
                />
              </Grid.Col>
            </Grid>
          </AppShell.Main>
          <AppShell.Aside p="md">
            <Flex justify="space-between" gap={8}>
              <Button
                variant="transparent"
                styles={{ color: 'rgba(209, 33, 26, 1)' }}
                leftSection={<FiX />}
                onClick={onCancel}
              >
                Cancel
              </Button>

              <Button
                variant="solid"
                leftSection={<FiEdit3 />}
                onClick={onUpdate}
              >
                Update
              </Button>
            </Flex>

            <Text fw={700} fz={14} my={8}>
              Widget Setting
            </Text>
            {itemsList.length > 0 && (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided, snapshot) => (
                    <Draggable
                      ref={provided.innerRef}
                      data={itemsList}
                      provided={provided}
                      snapshot={snapshot}
                      onHide={handleHideItem}
                    />
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </AppShell.Aside>
        </AppShell>
      </AdminLayout>

      <ModalConfirmation
        isVisible={openModal}
        onCancel={() => toggleModal()}
        onConfirm={handleUpdateWidget}
        type="edit"
      />

      <LoadingOverlay
        visible={
          fetchedConfigurationDashboard.isLoading ||
          updateWidgetConfiguration.isLoading
        }
      />
    </>
  );
};

export default DashboardPrincipalPage;
